import "../styles/TermsPrivacy.css";

const WEBSITE_URL = "https://www.kennethlpowell.com";
const CONTACT_EMAIL = "author@kennethlpowell.com";
const LAST_UPDATED = "2026-10-24";

export default function Privacy() {
  return (
    <main className="legal-page-container">
      <header className="legal-page-header">
        <h1>Privacy Policy</h1>

        <p className="legal-page-updated">
          Last updated:{" "}
          <time dateTime={LAST_UPDATED}>October 24, 2026</time>
        </p>
      </header>

      <section aria-labelledby="privacy-who-we-are">
        <h2 id="privacy-who-we-are">Who We Are</h2>

        <p>
          This website is operated by Kenneth L. Powell (&quot;we,&quot;
          &quot;us,&quot; or &quot;our&quot;). Our website address is{" "}
          <a
            href={WEBSITE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            kennethlpowell.com
          </a>
          .
        </p>
      </section>

      <section aria-labelledby="privacy-information-collected">
        <h2 id="privacy-information-collected">
          What Information We Collect
        </h2>

        <ul>
          <li>
            <strong>Contact information:</strong> Your name and email
            address when you subscribe to newsletters, submit forms, or
            contact us.
          </li>

          <li>
            <strong>Usage data:</strong> Information about how you use
            the website, which may include your IP address, browser type,
            device information, and browsing activity.
          </li>

          <li>
            <strong>Social media data:</strong> Public profile
            information or other data that a social platform permits you
            to share when you interact with our social media accounts.
          </li>

          <li>
            <strong>Purchase information:</strong> Information needed to
            process orders, provide purchased products, and maintain
            transaction records. Payment-card details may be processed
            directly by third-party payment providers rather than stored
            by us.
          </li>
        </ul>
      </section>

      <section aria-labelledby="privacy-use-information">
        <h2 id="privacy-use-information">
          How We Use Your Information
        </h2>

        <ul>
          <li>Provide, maintain, and improve the website.</li>
          <li>Respond to questions, messages, and support requests.</li>
          <li>Send newsletters and updates when you have subscribed.</li>
          <li>Process purchases and provide products or services.</li>
          <li>Improve our content and user experience.</li>
          <li>
            Maintain the security and proper operation of the website.
          </li>
          <li>
            Comply with legal, accounting, and reporting obligations.
          </li>
        </ul>
      </section>

      <section aria-labelledby="privacy-cookies">
        <h2 id="privacy-cookies">
          Cookies and Similar Technologies
        </h2>

        <p>
          We may use cookies and similar technologies to maintain
          website functionality, understand how visitors use the site,
          remember preferences, and improve the user experience. You can
          configure your browser to refuse cookies or notify you when a
          cookie is being sent. Disabling certain cookies may affect
          website functionality.
        </p>
        <p>
          Our website analytics use a random, tab-scoped visit identifier
          to understand book views, outbound links, newsletter signups,
          ebook downloads, and activity on the public Avwakin Development
          and Avwakin Entertainment pages, including video-tile and YouTube-link
          clicks. A visit expires after 30 minutes of inactivity. We record
          page paths and campaign labels, not full referrer URLs, query
          strings, form contents, email addresses, account identifiers,
          or IP addresses in our analytics event records. Hosting and
          security logs are separate from these records.
        </p>
        <p>
          Browser analytics are skipped for signed-in sessions and
          private areas. We honor Do Not Track and Global Privacy Control
          signals for this analytics collection. Campaign labels must
          not contain personal information.
        </p>
      </section>

      <section aria-labelledby="privacy-sharing">
        <h2 id="privacy-sharing">
          Who We Share Your Information With
        </h2>

        <p>
          We do not sell your personal information. We may share
          information with:
        </p>

        <ul>
          <li>
            Service providers that assist with website hosting, email
            delivery, analytics, payments, order fulfillment, or other
            business operations.
          </li>

          <li>
            Government authorities or other parties when disclosure is
            required by law or reasonably necessary to protect our
            rights, users, or property.
          </li>

          <li>
            A successor organization if the website or related business
            assets are transferred, reorganized, or sold.
          </li>
        </ul>
      </section>

      <section aria-labelledby="privacy-retention">
        <h2 id="privacy-retention">
          How Long We Retain Your Information
        </h2>

        <p>
          We retain personal information only for as long as reasonably
          necessary to fulfill the purposes described in this policy,
          provide requested services, resolve disputes, enforce
          agreements, and satisfy legal, accounting, or reporting
          requirements.
        </p>
        <p>
          Detailed website analytics events are retained for up to
          90 days and removed by scheduled cleanup. This limit does not
          apply to separate subscriber, purchase, or email-delivery
          records. Backup copies follow their separate backup lifecycle.
        </p>
      </section>

      <section aria-labelledby="privacy-security">
        <h2 id="privacy-security">Data Security</h2>

        <p>
          We use reasonable administrative and technical measures to
          protect personal information. However, no internet
          transmission or electronic storage method can be guaranteed to
          be completely secure.
        </p>
      </section>

      <section aria-labelledby="privacy-rights">
        <h2 id="privacy-rights">Your Privacy Rights</h2>

        <p>
          Depending on where you live, you may have the right to:
        </p>

        <ul>
          <li>Request access to personal information we hold about you.</li>
          <li>Request correction of inaccurate information.</li>
          <li>Request deletion of certain personal information.</li>
          <li>Object to or restrict certain processing activities.</li>
          <li>Withdraw consent when processing is based on consent.</li>
          <li>Unsubscribe from marketing emails at any time.</li>
        </ul>

        <p>
          To submit a privacy-related request, contact us using the
          information at the bottom of this page. We may need to verify
          your identity before completing certain requests.
        </p>
      </section>

      <section aria-labelledby="privacy-third-party-links">
        <h2 id="privacy-third-party-links">
          Third-Party Websites and Services
        </h2>

        <p>
          The website may contain links to third-party websites or
          services. We are not responsible for the privacy practices,
          security, or content of those third parties. Review their
          privacy policies before providing personal information.
        </p>
      </section>

      <section aria-labelledby="privacy-children">
        <h2 id="privacy-children">Children&apos;s Privacy</h2>

        <p>
          This website is not directed toward children under the age of
          13, and we do not knowingly collect personal information from
          children under 13. If you believe a child has provided
          personal information through the website, please contact us so
          we can review and remove it where appropriate.
        </p>
      </section>

      <section aria-labelledby="privacy-changes">
        <h2 id="privacy-changes">
          Changes to This Privacy Policy
        </h2>

        <p>
          We may update this Privacy Policy from time to time. Any
          changes will be posted on this page, and the revision date at
          the top of the policy will be updated.
        </p>
      </section>

      <section aria-labelledby="privacy-contact">
        <h2 id="privacy-contact">Contact Us</h2>

        <p>
          Contact us with questions, concerns, or privacy-related
          requests:
        </p>

        <address className="legal-contact-information">
          <p>
            <strong>Email:</strong>{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>
              {CONTACT_EMAIL}
            </a>
          </p>

          <p>
            <strong>Website:</strong>{" "}
            <a
              href={WEBSITE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              kennethlpowell.com
            </a>
          </p>
        </address>
      </section>
    </main>
  );
}
